import { observer } from 'mobx-react-lite';
import { useAppStateContext } from 'src/AppState/AppState';
import React, { useState } from 'react';
import { CategoriesAndGamesCollection } from 'src/domains/casino/state/AppCasino/store/game-store/Game.store';
import { ImageUpload } from './ImageUpload/ImageUpload';
import { GameState } from 'src/domains/casino/state/AppCasino/live-games/Game.state';
import { Switcher } from 'src/components/CmsCasinoManagement/EditFormComponents/Switcher/Switcher';
import { SelectMultiple } from 'src/components/CmsCasinoManagement/EditFormComponents/SelectMultiple/SelectMultiple';
import {
    ActionsWrapper,
    BlurleyWrapper,
    Button,
    FormElements,
    GameEditFormWrapper,
    Left,
    Right,
    StatusSelect,
    SwitcherWrapper,
    UploadWrapper,
} from './GameEditForm.style';
import { CollectionType } from 'src/domains/casino/models/models';
import { Input } from 'src/domains/layouts/shared/formComponents/input/Input';
import { Textarea } from 'src/domains/layouts/shared/formComponents/textarea/Textarea';
import { Label } from 'src/domains/layouts/shared/typography/Content.style';
import { InputLabel } from 'src/domains/layouts/shared/formComponents/FormComponents.style';
import { MobxMapAutoNew } from 'src_common/common/mobx-utils/MobxMapAutoNew';
import { Loader, PreloaderBlockFixed } from 'src/domains/casino/components/games/GamesPage.style';
import { useCommon } from 'src/AppState/Common';

interface FormPropsType {
    state: GameState;
    collectionSlug: CollectionType;
}

const Form = observer((props: FormPropsType) => {
    const { state, collectionSlug } = props;
    const isTrendingCategory = state.gameHasSelectedCategoryName('Trending');

    return (
        <FormElements>
            <Input
                label='Game Name'
                formInputState={state.gameName}
                disabled
            />
            <Input
                label='Game ID:'
                formInputState={state.gameIdInput}
                disabled
            />

            <InputLabel>Category:</InputLabel>
            <SelectMultiple
                label='category'
                placeholder='Select categories'
                minSelections={1}
                maxSelections={5}
                state={state.categories}
                options={state.categoryOptions}
                dataTest='category-select'
            />

            <SwitcherWrapper>
                <Label>Demo mode</Label>
                <Switcher state={state.isDemo} />
            </SwitcherWrapper>

            {collectionSlug !== 'virtuals' && (
                <>
                    <Input
                        inputType='number'
                        label='Max Bet:'
                        min='0'
                        max='100'
                        step='0.1'
                        formInputState={state.maxBet}
                        dataTest='max-bet-input'
                    />
                    <Input
                        inputType='number'
                        label='Min Bet:'
                        min='0'
                        max='100'
                        step='0.1'
                        formInputState={state.minBet}
                        dataTest='min-bet-input'
                    />
                    <Input
                        inputType='number'
                        label='Max Win:'
                        min='0'
                        max='10000'
                        step='0.1'
                        formInputState={state.maxWin}
                    />
                    <Input
                        inputType='number'
                        label='Return To Player:'
                        min='0'
                        max='100'
                        step='0.1'
                        formInputState={state.returnToPlayer}
                    />
                </>
            )}

            <Textarea
                label='Game Description'
                formState={state.description}
            />

            <UploadWrapper>
                <Label>
                    Choose artwork <span>(horizontal rectangle dimensions)</span>:
                </Label>
                <ImageUpload
                    game={state.game}
                    uploaderState={state.rectangleImageUploader}
                    wrapperDimensions='horizontal'
                />
            </UploadWrapper>

            {isTrendingCategory ? (
                <UploadWrapper>
                    <>
                        <Label>
                            Choose artwork <span>(vertical rectangle dimensions)</span>:
                        </Label>
                        <ImageUpload
                            game={state.game}
                            uploaderState={state.rectangleVerticalImageUploader}
                            wrapperDimensions='vertical'
                        />
                    </>
                </UploadWrapper>
            ) : null}

            <UploadWrapper>
                <Label>
                    Choose artwork <span>(square dimensions)</span>:
                </Label>
                <ImageUpload
                    game={state.game}
                    uploaderState={state.squareImageUploader}
                    wrapperDimensions='square'
                />
            </UploadWrapper>
        </FormElements>
    );
});

interface ActionsPropsType {
    gameState: GameState;
    collection: CollectionType;
}

const Actions = observer((props: ActionsPropsType) => {
    const { appCasinoState } = useAppStateContext();
    const { redirectState } = appCasinoState;
    const { gameState, collection } = props;

    return (
        <ActionsWrapper>
            <Left>
                <Button
                    data-test='back-button'
                    onClick={() => redirectState.redirectToCollection(collection)}
                >
                    &#8592; Back
                </Button>
            </Left>

            <Right>
                <StatusSelect
                    defaultValue={gameState.isGamePublished ? 'Live' : 'NotPublished'}
                    key='status'
                    disabled
                >
                    <option value='Live'>Status: Live</option>
                    <option value='NotPublished'>Status: Not published</option>
                </StatusSelect>
                <Button
                    key='cancel'
                    onClick={gameState.resetForm}
                    data-test='cancel-button'
                >
                    Cancel
                </Button>
                <Button
                    key='save-changes'
                    onClick={gameState.handleSubmit}
                    data-test='save-button'
                >
                    Save Changes
                </Button>

                <Button
                    key='delete'
                    onClick={gameState.handleDeleteGame}
                >
                    Delete
                </Button>

                {!gameState.isGamePublished && (
                    <Button
                        key='publish'
                        onClick={gameState.publishGame}
                    >
                        Publish
                    </Button>
                )}
                {gameState.isGamePublished && (
                    <Button
                        key='unpublish'
                        onClick={gameState.unpublishGame}
                    >
                        Not published
                    </Button>
                )}
            </Right>
        </ActionsWrapper>
    );
});

interface GameEditFormPropsType {
    collection: CategoriesAndGamesCollection;
    gameId: number;
}

export const GameEditForm = observer((props: GameEditFormPropsType) => {
    const { collection, gameId } = props;
    const { appCasinoState } = useAppStateContext();
    const common = useCommon();
    const [gameStateMap] = useState(
        () =>
            new MobxMapAutoNew(
                (gameId: number) => new GameState(gameId, collection, appCasinoState.redirectState, common)
            )
    );

    const gameState = gameStateMap.get(gameId);

    const renderLoading = (): React.ReactElement | null => {
        if (gameState.isLoading === false) {
            return null;
        }

        return (
            <PreloaderBlockFixed data-test='game-edit-form-loader'>
                <Loader />
            </PreloaderBlockFixed>
        );
    };

    return (
        <GameEditFormWrapper>
            {renderLoading()}

            <BlurleyWrapper isBlur={gameState.isLoading}>
                <Actions
                    gameState={gameState}
                    collection={collection.collection}
                />
                <Form
                    state={gameState}
                    collectionSlug={collection.collection}
                />
                <Actions
                    gameState={gameState}
                    collection={collection.collection}
                />
            </BlurleyWrapper>
        </GameEditFormWrapper>
    );
});
